import React, { useState, useEffect, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container, Stack, Typography, Breadcrumbs, Box, Paper,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, Button, Chip, CircularProgress, Alert,
  TextField, FormControl, InputLabel, Select, MenuItem, Grid,
  Tooltip, Collapse, IconButton, Divider,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DownloadRounded,
  ExpandMoreRounded, ExpandLessRounded, FilterListRounded,
} from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── constants ───────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  open:           "error",
  "under-review": "warning",
  closed:         "success",
  voided:         "default",
};

const DISPOSITION_LABEL = {
  pending:            "Pending",
  "use-as-is":        "Use As-Is",
  rework:             "Rework",
  scrap:              "Scrap",
  "return-to-vendor": "Return to Vendor",
};

const DISPOSITION_COLOR = {
  pending:            "default",
  "use-as-is":        "info",
  rework:             "warning",
  scrap:              "error",
  "return-to-vendor": "secondary",
};

// ─── csv export ──────────────────────────────────────────────────────────────

function exportCSV(ncrs) {
  const header = [
    "NCR Number", "Raised Date", "Status", "Disposition",
    "Job", "Part Description", "Part Number", "Heat Number",
    "Qty Affected", "Description", "Root Cause", "Corrective Action",
    "Raised By", "Assigned To", "Closed By", "Closed At",
  ];
  const rows = ncrs.map((n) => [
    n.ncr_number, n.raised_date, n.status, n.disposition,
    n.job_number ? `${n.job_number} ${n.job_name || ""}`.trim() : "",
    n.part_description || "", n.part_number || "", n.heat_number || "",
    n.quantity_affected ?? "",
    n.description, n.root_cause || "", n.corrective_action || "",
    n.raised_by_name || "", n.assigned_to_name || "",
    n.closed_by_name || "", n.closed_at ? new Date(n.closed_at).toLocaleDateString() : "",
  ]);
  const csv = [header, ...rows]
    .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: url, download: "ncr-summary.csv" });
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ─── tile ─────────────────────────────────────────────────────────────────────

function Tile({ label, count, color }) {
  const colors = {
    error:   { bg: "#FDECEA", text: "#8B1A1A" },
    warning: { bg: "#FFF3CD", text: "#7B4F00" },
    success: { bg: "#D6F0E0", text: "#1E6B3C" },
    default: { bg: "#F5F5F5", text: "#555555" },
    primary: { bg: "#D6E4F0", text: "#1B4F8A" },
  };
  const c = colors[color] || colors.default;
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: "center", bgcolor: c.bg, borderColor: c.bg }}>
      <Typography variant="h4" fontWeight={700} color={c.text}>{count}</Typography>
      <Typography variant="caption" color={c.text} fontWeight={600} textTransform="uppercase"
        letterSpacing={0.8}>{label}</Typography>
    </Paper>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

export default function NcrSummaryReport() {
  const navigate = useNavigate();

  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expanded,    setExpanded]    = useState({});

  const [filters, setFilters] = useState({
    status: "", disposition: "", dateFrom: "", dateTo: "",
  });

  const fetch = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (filters.status)      params.append("status",      filters.status);
    if (filters.disposition) params.append("disposition", filters.disposition);
    if (filters.dateFrom)    params.append("dateFrom",    filters.dateFrom);
    if (filters.dateTo)      params.append("dateTo",      filters.dateTo);
    ApiCall.get(`/reports/ncr-summary?${params}`)
      .then((r) => { setData(r.data?.data); setPage(0); })
      .catch(() => setError("Failed to load NCR Summary Report"))
      .finally(() => setLoading(false));
  }, [filters]);

  // Load on mount
  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const ncrs = data?.ncrs || [];
  const paginated = ncrs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Helmet><title>NCR / CAPA Summary | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Breadcrumbs>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HomeRounded fontSize="small" />
              <Typography variant="body2" color="text.secondary">Reports</Typography>
            </Stack>
            <Typography variant="body2" fontWeight={600}>NCR / CAPA Summary</Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            {data && ncrs.length > 0 && (
              <Button variant="outlined" size="small" startIcon={<DownloadRounded />}
                onClick={() => exportCSV(ncrs)}>
                Export CSV
              </Button>
            )}
            <Button variant="outlined" size="small" startIcon={<FilterListRounded />}
              onClick={() => setFiltersOpen((p) => !p)}
              color={Object.values(filters).some(Boolean) ? "primary" : "inherit"}>
              Filters{Object.values(filters).some(Boolean) ? " (active)" : ""}
            </Button>
            <Button variant="text" size="small" startIcon={<ArrowBackRounded />}
              onClick={() => navigate("/reports")}>
              All Reports
            </Button>
          </Stack>
        </Stack>
        <PageGuide
          why="See all your non-conformances and corrective actions in one rolled-up view."
          who="The QM and management, reviewing non-conformance trends."
          what="A summary of NCRs and CAPAs across jobs, filterable and exportable."
          how="Filter by job or status, then export to CSV for review or the customer."
          when="For quality reviews and audit preparation."
          standard="AISC GR §6.1 · ISO 9001:2015 §10.2"
        />

        {/* ── filters panel ── */}
        <Collapse in={filtersOpen}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={filters.status} label="Status"
                    onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                    <MenuItem value="">All</MenuItem>
                    {["open","under-review","closed","voided"].map((s) =>
                      <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Disposition</InputLabel>
                  <Select value={filters.disposition} label="Disposition"
                    onChange={(e) => setFilters((p) => ({ ...p, disposition: e.target.value }))}>
                    <MenuItem value="">All</MenuItem>
                    {Object.entries(DISPOSITION_LABEL).map(([v, l]) =>
                      <MenuItem key={v} value={v}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth size="small" label="From" type="date"
                  InputLabelProps={{ shrink: true }} value={filters.dateFrom}
                  onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth size="small" label="To" type="date"
                  InputLabelProps={{ shrink: true }} value={filters.dateTo}
                  onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" size="small" onClick={fetch} fullWidth>Apply</Button>
                  <Button variant="outlined"  size="small" fullWidth
                    onClick={() => { setFilters({ status: "", disposition: "", dateFrom: "", dateTo: "" }); }}>
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* ── summary tiles ── */}
        {data && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3} md={2}><Tile label="Total" count={data.counts.total} color="primary" /></Grid>
            <Grid item xs={6} sm={3} md={2}><Tile label="Open" count={data.counts.open || 0} color="error" /></Grid>
            <Grid item xs={6} sm={3} md={2}><Tile label="Under Review" count={data.counts["under-review"] || 0} color="warning" /></Grid>
            <Grid item xs={6} sm={3} md={2}><Tile label="Closed" count={data.counts.closed || 0} color="success" /></Grid>
            <Grid item xs={6} sm={3} md={2}><Tile label="Voided" count={data.counts.voided || 0} color="default" /></Grid>
            <Grid item xs={6} sm={3} md={2}><Tile label="Filtered" count={ncrs.length} color="primary" /></Grid>
          </Grid>
        )}

        {/* ── states ── */}
        {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading…</Typography></Box>}

        {!loading && data && ncrs.length === 0 && (
          <Alert severity="info">No NCRs match the current filters.</Alert>
        )}

        {/* ── table ── */}
        {!loading && ncrs.length > 0 && (
          <>
            <TableContainer component={Paper} variant="outlined">
              <SmartTable tableId="report-ncr-summary" lockedColumns={["NCR #", "Status", "Disposition"]} size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell width={32} />
                    <TableCell><strong>NCR #</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Disposition</strong></TableCell>
                    <TableCell><strong>Job</strong></TableCell>
                    <TableCell><strong>Part</strong></TableCell>
                    <TableCell><strong>Raised By</strong></TableCell>
                    <TableCell><strong>Closed</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((n) => (
                    <React.Fragment key={n.id}>
                      <TableRow hover sx={{ "& td": { borderBottom: expanded[n.id] ? "none" : undefined } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleExpand(n.id)}>
                            {expanded[n.id] ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontFamily: "monospace" }}>{n.ncr_number}</TableCell>
                        <TableCell>{n.raised_date ? new Date(n.raised_date).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>
                          <Chip label={prettyStatus(n.status)} color={STATUS_COLOR[n.status] || "default"} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={DISPOSITION_LABEL[n.disposition] || n.disposition}
                            color={DISPOSITION_COLOR[n.disposition] || "default"} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{n.job_number || "—"}</TableCell>
                        <TableCell>
                          <Tooltip title={n.part_description || ""}>
                            <span>{n.part_number || n.part_description || "—"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{n.raised_by_name || "—"}</TableCell>
                        <TableCell>{n.closed_at ? new Date(n.closed_at).toLocaleDateString() : "—"}</TableCell>
                      </TableRow>

                      {/* ── expand row ── */}
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                          <Collapse in={!!expanded[n.id]} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 4, py: 2, bgcolor: "grey.50", borderBottom: "1px solid #e0e0e0" }}>
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">Description</Typography>
                                  <Typography variant="body2" mt={0.5}>{n.description || "—"}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">Root Cause</Typography>
                                  <Typography variant="body2" mt={0.5}>{n.root_cause || "Not yet identified"}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">Corrective Action</Typography>
                                  <Typography variant="body2" mt={0.5}>{n.corrective_action || "Not yet assigned"}</Typography>
                                </Grid>
                                {(n.heat_number || n.purchase_order_no || n.quantity_affected != null) && (
                                  <Grid item xs={12}>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Stack direction="row" spacing={3}>
                                      {n.heat_number      && <Typography variant="body2"><strong>Heat:</strong> {n.heat_number}</Typography>}
                                      {n.purchase_order_no && <Typography variant="body2"><strong>PO:</strong> {n.purchase_order_no}</Typography>}
                                      {n.quantity_affected != null && <Typography variant="body2"><strong>Qty Affected:</strong> {n.quantity_affected}</Typography>}
                                      {n.assigned_to_name && <Typography variant="body2"><strong>Assigned To:</strong> {n.assigned_to_name}</Typography>}
                                    </Stack>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </SmartTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={ncrs.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Container>
    </>
  );
}
